/**
 * M488.3 — o corpo de uma mensagem da conversa, em nós tipados.
 *
 * A conversa renderizava `white-space: pre-wrap` em fonte proporcional: título, lista, tabela e bloco de
 * código chegavam como parágrafo corrido, e código saía em fonte de texto. O agente responde em Markdown, e
 * o dono relatou a leitura como "sem formatação nenhuma praticamente".
 *
 * **Por que um renderer próprio e não uma biblioteca.** O `packages/web` tem quatro dependências de runtime
 * (react, react-dom, zustand, xterm) num produto local-first; uma árvore `react-markdown` + `remark-gfm`
 * traria dezenas de transitivas para resolver um subset pequeno e conhecido. E o ganho principal não é
 * tamanho: este módulo devolve uma ÁRVORE TIPADA que o React monta como elementos — nunca string de HTML,
 * nunca `dangerouslySetInnerHTML`. A superfície de XSS deixa de existir por construção, em vez de depender
 * de um sanitizador correto.
 *
 * **O subset é fechado de propósito.** Cobre o que um agente de fato escreve: bloco de código, código
 * inline, títulos, listas, ênfase, link, citação, tabela e regra horizontal. HTML embutido NÃO é
 * interpretado — vira texto, que é o comportamento seguro e também o honesto.
 *
 * **Entrada incompleta é caso NORMAL, não borda.** O texto chega cortado em `maxCharsPerEntry` (64 KB) antes
 * de passar por aqui, então uma cerca de código sem fechamento é rotina: ela termina no fim do texto em vez
 * de engolir o resto. Nenhuma entrada malformada pode lançar.
 */
const FENCE = /^(\s*)(`{3,}|~{3,})\s*([A-Za-z0-9_+.-]*)\s*$/u;
const HEADING = /^ {0,3}(#{1,6})\s+(.*)$/u;
const RULE = /^ {0,3}([-*_])(?:\s*\1){2,}\s*$/u;
const UL_ITEM = /^(\s*)[-*+]\s+(.*)$/u;
const OL_ITEM = /^(\s*)\d{1,9}[.)]\s+(.*)$/u;
const QUOTE = /^ {0,3}>\s?(.*)$/u;
const TABLE_DELIM = /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/u;
/** Só protocolos que um link de conversa legitimamente usa. O resto vira texto. */
function safeHref(raw) {
    const href = raw.trim();
    if (href.length === 0 || href.length > 2048)
        return null;
    if (/^(https?:\/\/|mailto:)/iu.test(href))
        return href;
    if (href.startsWith('/') && !href.startsWith('//'))
        return href;
    return null;
}
function splitTableRow(line) {
    let body = line.trim();
    if (body.startsWith('|'))
        body = body.slice(1);
    if (body.endsWith('|') && !body.endsWith('\\|'))
        body = body.slice(0, -1);
    const cells = [];
    let current = '';
    for (let i = 0; i < body.length; i += 1) {
        const ch = body[i];
        if (ch === '\\' && body[i + 1] === '|') {
            current += '|';
            i += 1;
            continue;
        }
        if (ch === '|') {
            cells.push(current.trim());
            current = '';
            continue;
        }
        current += ch;
    }
    cells.push(current.trim());
    return cells;
}
function pushText(out, value) {
    if (value.length === 0)
        return;
    const last = out.at(-1);
    if (last && last.type === 'text')
        out[out.length - 1] = { type: 'text', value: last.value + value };
    else
        out.push({ type: 'text', value });
}
/**
 * Inline em uma passada. Delimitador que não fecha é texto literal — é o que mantém a entrada truncada
 * legível em vez de fazer metade da mensagem virar ênfase.
 */
/**
 * Acha o delimitador que FECHA a ênfase: ele não pode ter espaço antes (senão `a * b * c` fecharia) nem,
 * no caso do `_`, estar colado a letra ou dígito (senão `foo_bar_baz` fecharia dentro da palavra).
 */
function findClosingDelimiter(source, marker, from, ch) {
    let at = source.indexOf(marker, from);
    while (at >= 0) {
        const before = source[at - 1] ?? ' ';
        const after = source[at + marker.length] ?? ' ';
        const closes = !/\s/u.test(before) && (ch === '*' || !/[\p{L}\p{N}]/u.test(after));
        if (closes && at > from)
            return at;
        at = source.indexOf(marker, at + marker.length);
    }
    return -1;
}
export function parseInline(source) {
    const out = [];
    let i = 0;
    while (i < source.length) {
        const ch = source[i];
        if (ch === '`') {
            let ticks = 0;
            while (source[i + ticks] === '`')
                ticks += 1;
            const open = '`'.repeat(ticks);
            const close = source.indexOf(open, i + ticks);
            if (close > 0) {
                out.push({ type: 'code', value: source.slice(i + ticks, close) });
                i = close + ticks;
                continue;
            }
            pushText(out, open);
            i += ticks;
            continue;
        }
        if (ch === '[') {
            const closeBracket = source.indexOf(']', i + 1);
            if (closeBracket > 0 && source[closeBracket + 1] === '(') {
                const closeParen = source.indexOf(')', closeBracket + 2);
                if (closeParen > 0) {
                    const href = safeHref(source.slice(closeBracket + 2, closeParen));
                    const label = source.slice(i + 1, closeBracket);
                    if (href !== null) {
                        out.push({ type: 'link', href, children: parseInline(label) });
                        i = closeParen + 1;
                        continue;
                    }
                }
            }
            pushText(out, ch);
            i += 1;
            continue;
        }
        if (ch === '*' || ch === '_') {
            const double = source[i + 1] === ch;
            const marker = double ? ch + ch : ch;
            // Ênfase só ABRE se houver conteúdo colado ao delimitador e ela não estiver no meio de uma palavra.
            // Sem isto, `session_id`, `foo_bar_baz` e `2 * 3 * 4` viravam itálico — e conversa de agente é cheia
            // de snake_case e de multiplicação. É a regra de "flanking" do CommonMark, na forma mínima que este
            // subset precisa.
            const before = i > 0 ? source[i - 1] : ' ';
            const after = source[i + marker.length] ?? ' ';
            const opens = !/\s/u.test(after) && (ch === '*' || !/[\p{L}\p{N}]/u.test(before));
            if (opens) {
                const close = findClosingDelimiter(source, marker, i + marker.length, ch);
                if (close > i + marker.length) {
                    const inner = source.slice(i + marker.length, close);
                    out.push(double
                        ? { type: 'strong', children: parseInline(inner) }
                        : { type: 'em', children: parseInline(inner) });
                    i = close + marker.length;
                    continue;
                }
            }
            pushText(out, ch);
            i += 1;
            continue;
        }
        pushText(out, ch);
        i += 1;
    }
    return out;
}
function indentOf(line) {
    let n = 0;
    while (line[n] === ' ')
        n += 1;
    return n;
}
/** Converte o corpo de uma mensagem em blocos. Nunca lança; entrada vazia devolve lista vazia. */
export function parseConversationMarkdown(source) {
    if (typeof source !== 'string' || source.length === 0)
        return [];
    const lines = source.split('\n');
    const blocks = [];
    let paragraph = [];
    const flushParagraph = () => {
        if (paragraph.length === 0)
            return;
        const text = paragraph.join('\n').trim();
        paragraph = [];
        if (text.length > 0)
            blocks.push({ type: 'paragraph', children: parseInline(text) });
    };
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        const fence = FENCE.exec(line);
        if (fence) {
            flushParagraph();
            const marker = fence[2];
            const lang = fence[3] || null;
            const body = [];
            let closed = false;
            i += 1;
            while (i < lines.length) {
                const candidate = lines[i];
                const closing = FENCE.exec(candidate);
                if (closing && closing[2].startsWith(marker[0]) && closing[2].length >= marker.length && !closing[3]) {
                    closed = true;
                    i += 1;
                    break;
                }
                body.push(candidate);
                i += 1;
            }
            blocks.push({ type: 'code', lang, value: body.join('\n'), closed });
            continue;
        }
        if (line.trim().length === 0) {
            flushParagraph();
            i += 1;
            continue;
        }
        if (RULE.test(line)) {
            flushParagraph();
            blocks.push({ type: 'rule' });
            i += 1;
            continue;
        }
        const heading = HEADING.exec(line);
        if (heading) {
            flushParagraph();
            const level = Math.min(6, heading[1].length);
            blocks.push({ type: 'heading', level, children: parseInline(heading[2].replace(/\s+#+\s*$/u, '').trim()) });
            i += 1;
            continue;
        }
        const quote = QUOTE.exec(line);
        if (quote) {
            flushParagraph();
            const body = [quote[1]];
            i += 1;
            while (i < lines.length) {
                const next = QUOTE.exec(lines[i]);
                if (!next)
                    break;
                body.push(next[1]);
                i += 1;
            }
            blocks.push({ type: 'quote', children: parseConversationMarkdown(body.join('\n')) });
            continue;
        }
        // Tabela: cabeçalho + linha de delimitação. Sem a delimitação é parágrafo com pipes, não tabela.
        if (line.includes('|') && i + 1 < lines.length && TABLE_DELIM.test(lines[i + 1])) {
            flushParagraph();
            const header = splitTableRow(line).map((cell) => parseInline(cell));
            i += 2;
            const rows = [];
            while (i < lines.length && lines[i].includes('|') && lines[i].trim().length > 0) {
                rows.push(splitTableRow(lines[i]).map((cell) => parseInline(cell)));
                i += 1;
            }
            blocks.push({ type: 'table', header, rows });
            continue;
        }
        const ul = UL_ITEM.exec(line);
        const ol = OL_ITEM.exec(line);
        if (ul || ol) {
            flushParagraph();
            const ordered = ol !== null && ul === null;
            const baseIndent = indentOf(line);
            const items = [];
            while (i < lines.length) {
                const candidate = lines[i];
                const itemUl = UL_ITEM.exec(candidate);
                const itemOl = OL_ITEM.exec(candidate);
                const match = ordered ? itemOl : itemUl;
                if (!match || indentOf(candidate) !== baseIndent) {
                    // Linha de continuação (mais indentada) pertence ao item corrente.
                    if (items.length > 0 && candidate.trim().length > 0 && indentOf(candidate) > baseIndent) {
                        const target = items[items.length - 1];
                        const last = target.at(-1);
                        if (last && last.type === 'paragraph') {
                            target[target.length - 1] = {
                                type: 'paragraph',
                                children: parseInline(`${inlineToPlainText(last.children)} ${candidate.trim()}`),
                            };
                        }
                        else {
                            target.push({ type: 'paragraph', children: parseInline(candidate.trim()) });
                        }
                        i += 1;
                        continue;
                    }
                    break;
                }
                items.push(parseConversationMarkdown(match[2]));
                i += 1;
            }
            blocks.push({ type: 'list', ordered, items });
            continue;
        }
        paragraph.push(line);
        i += 1;
    }
    flushParagraph();
    return blocks;
}
/**
 * Texto puro de uma sequência inline. É o que permite a BUSCA continuar funcionando sobre a árvore: o
 * destaque precisa saber onde o texto está, e o filtro por entrada continua casando sobre o texto plano.
 */
export function inlineToPlainText(nodes) {
    let out = '';
    for (const node of nodes) {
        if (node.type === 'text' || node.type === 'code')
            out += node.value;
        else
            out += inlineToPlainText(node.children);
    }
    return out;
}
/** Texto puro de uma árvore de blocos — mesma razão do `inlineToPlainText`. */
export function blocksToPlainText(blocks) {
    const parts = [];
    for (const block of blocks) {
        if (block.type === 'paragraph' || block.type === 'heading')
            parts.push(inlineToPlainText(block.children));
        else if (block.type === 'code')
            parts.push(block.value);
        else if (block.type === 'quote')
            parts.push(blocksToPlainText(block.children));
        else if (block.type === 'rule')
            parts.push('');
        else if (block.type === 'list')
            for (const item of block.items)
                parts.push(blocksToPlainText(item));
        else {
            for (const cell of block.header)
                parts.push(inlineToPlainText(cell));
            for (const row of block.rows)
                for (const cell of row)
                    parts.push(inlineToPlainText(cell));
        }
    }
    return parts.join('\n');
}
//# sourceMappingURL=conversation-markdown.js.map